import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
// `@github-ui/issue-viewer` still carries a Relay dependency, which this rule exists to contain.
// This route is the migration off it; the leaves that still reach for Relay are documented on
// DataRouterIssueViewer. Lift once they are unpicked -- github/issues#22378.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {DataRouterIssueViewer} from '@github-ui/issue-viewer/DataRouterIssueViewer'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY} from '@github-ui/issue-viewer/IssueViewerNavigation'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ISSUE_VIEWER_DEFAULT_CONFIG} from '@github-ui/issue-viewer/OptionConfig'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {applyPendingSuggestionOperation} from '@github-ui/issue-viewer/PendingSuggestionOperation'
import {applyIssueViewerFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
// This entry point carries no Relay dependency of its own; the restriction is package-wide.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useTimelinePrefill} from '@github-ui/issue-viewer/TimelinePrefill'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {RelayEnvironmentProvider} from '@github-ui/relay-environment'
import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {useLocation, useNavigate, useSearchParams} from '@github-ui/react-router'
import {AnalyticsWrapper as BaseAnalyticsWrapper} from '@github-ui/repo-issues-page-layout/AnalyticsWrapper'
import {parseSidePanelIssue, writeSidePanelIssueToUrl} from '@github-ui/issues-shared/side-panel-url'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {DashboardIssueSidePanel} from '../components/DashboardIssueSidePanel'
import {getIssuesRelayEnvironment} from '../components/issues-relay-environment'
import {issueViewerRoute} from './issue-viewer-route'
import {IssueSeoSchema} from './IssueSeoSchema'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

/**
 * The issue viewer on the Data Router, behind `issue_viewer_paved_path`.
 *
 * Everything rendered here comes from the route's page query, including the first timeline window,
 * so the page is complete after SSR.
 */
export function IssueViewer() {
  const payload = useRouteQuery(issueViewerRoute, 'mainQuery')
  const mainQueryTargets = useMemo(() => [{queryKey: payload.queryKey}], [payload.queryKey])
  const issueId = payload.data?.data?.repository?.issue?.id
  const projectItemsConnections = useMemo(
    () => (issueId ? [{ownerId: issueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}] : []),
    [issueId],
  )
  // The header and sidebar render from this payload, not the secondary view, so live updates need
  // this key to reach them.
  const routeQueryKey = useRouteQueryKey(issueViewerRoute, 'mainQuery')
  const renderedPayload = useOptimisticData(payload.data, {
    connections: projectItemsConnections,
    targets: mainQueryTargets,
    updateEntityFieldItems: applyPendingSuggestionOperation,
    applyEntityFieldItem: applyIssueViewerFieldValue,
  })
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const [searchParams] = useSearchParams()
  // `issues#show` still serves a positive `timeline_page` from the Relay viewer, so this is inert
  // until that fallback goes; the timeline hook reads it to fetch the crawler's isolated window.
  // Parsed strictly rather than with `parseInt`, which would accept `-1` and the `3` in `3junk` and
  // forward them to the endpoint as page numbers.
  const timelinePageParam = Number(searchParams.get('timeline_page'))
  const timelinePage = Number.isSafeInteger(timelinePageParam) && timelinePageParam > 0 ? timelinePageParam : undefined

  const [panelIssue, setPanelIssue] = useState<DashboardIssueIdentifier | null>(null)
  const panelReturnFocusRef = useRef<HTMLElement | null>(null)

  const showPanelIssue = useCallback((issue: DashboardIssueIdentifier | null) => {
    // Captured here because the click that opens the panel is what focus returns to on close.
    if (issue) panelReturnFocusRef.current = document.activeElement as HTMLElement | null
    startTransition(() => setPanelIssue(issue))
    writeSidePanelIssueToUrl(issue)
  }, [])
  const closePanel = useCallback(() => showPanelIssue(null), [showPanelIssue])

  // Post-mount, not a lazy initialiser: `ssrSafeLocation` resolves to the request URL on the
  // server, so the panel and its lazy Relay chunk would be pulled into SSR.
  useEffect(() => {
    const syncFromUrl = () => {
      const issue = parseSidePanelIssue()
      if (issue) panelReturnFocusRef.current = document.activeElement as HTMLElement | null
      setPanelIssue(issue)
    }
    syncFromUrl()
    // `writeSidePanelIssueToUrl` pushes history entries, so Back and Forward must move the panel
    // with them. Reads the URL rather than writing one, so it adds no entry of its own.
    window.addEventListener('popstate', syncFromUrl)
    return () => window.removeEventListener('popstate', syncFromUrl)
  }, [])

  const expandPanel = useCallback(() => startTransition(() => setPanelIssue(null)), [])

  // FORBIDDEN/SAML reloads the page; other errors throw to the boundary.
  const data = handleRepositoryDataError(renderedPayload)
  const repository = data?.repository
  const issue = repository?.issue
  const owner = issue?.repository.owner.login
  const repo = issue?.repository.name

  const optionConfig = useMemo(
    () => ({
      ...ISSUE_VIEWER_DEFAULT_CONFIG,
      navigate,
      navigateBack: () => {
        if (owner && repo) {
          navigate(`/${owner}/${repo}/issues`, {
            state: {[ISSUE_VIEWER_NAVIGATION_BYPASS_STATE_KEY]: pathname},
          })
        }
      },
      // The default config's `onClose: noop` is truthy and the header renders its close button on
      // that alone, so a full-page view must clear it or it shows a dead "Close panel" X.
      onClose: undefined,
      onSubIssueClick: ({owner: subOwner, repo: subRepo, number: subNumber}: DashboardIssueIdentifier) =>
        showPanelIssue({owner: subOwner, repo: subRepo, number: subNumber}),
      showIssueCreateButton: true,
      scrollToTopOnClick: true,
      timelineEventBaseUrl: owner && repo ? `/${owner}/${repo}/issues` : undefined,
      // This is a full-page view, like `IssueDetail`. The container-query layout keys off a named
      // container the legacy `IssueViewer` wrapper establishes and this route does not mount, so
      // without this the sidebar lays out under the timeline instead of beside it.
      useViewportQueries: true,
    }),
    [navigate, owner, pathname, repo, showPanelIssue],
  )

  // Deliberately the overlaid payload: the timeline renders from the cache, so an optimistic edit --
  // a pinned badge, a reaction -- reaches it by being written through here.
  const timelineIssueId = issue?.id
  const frontTimelineItems = issue?.timelineItems
  const backTimelineItems = issue?.backTimelineItems
  const timelineFrontIssue = useMemo(
    () =>
      timelineIssueId && frontTimelineItems ? {id: timelineIssueId, timelineItems: frontTimelineItems} : undefined,
    [timelineIssueId, frontTimelineItems],
  )
  const timelineBackIssue = useMemo(
    () => (timelineIssueId && backTimelineItems ? {id: timelineIssueId, timelineItems: backTimelineItems} : undefined),
    [timelineIssueId, backTimelineItems],
  )

  const issueNumber = issue?.number
  const timelineCoordinates = useMemo(
    () => (owner && repo && issueNumber !== undefined ? {owner, repo, number: issueNumber} : undefined),
    [owner, repo, issueNumber],
  )

  // Before the timeline renders, not after: a section that has already decided to fetch cannot be
  // talked out of it. Returns the cursor its newest window is pinned to.
  const initialBackAnchor = useTimelinePrefill({
    coordinates: timelineCoordinates,
    issue: timelineFrontIssue,
    backIssue: timelineBackIssue,
    payloadUpdatedAt: payload.dataUpdatedAt,
    enabled: !timelinePage,
  })

  // `handleRepositoryDataError` throws when the repository is missing, but a repository that
  // resolved without an issue reaches here -- rendering null would show a blank page that looks
  // like a successful load. `issues#show` 404s the cases we expect; this covers the rest.
  if (!issue) throw new Error(`Issue missing from repository data for ${pathname}`)

  return (
    <BaseAnalyticsWrapper category="Issue Show" appName="issues">
      <IssueSeoSchema
        title={issue.title}
        body={issue.body}
        url={issue.url}
        createdAt={issue.createdAt}
        author={issue.author?.login ? {login: issue.author.login, profileUrl: issue.author.profileUrl} : null}
      />
      {/* Shared, not per-mount: `key={issue.id}` would otherwise rebuild the store on every issue. */}
      <RelayEnvironmentProvider environment={getIssuesRelayEnvironment()}>
        <DataRouterIssueViewer
          key={issue.id}
          // The page query is the flattened `IssueViewerViewQuery`, so the payload is the shape the
          // viewer's components read -- minus the Relay fragment markers TypeScript still expects.
          issue={issue as unknown as Parameters<typeof DataRouterIssueViewer>[0]['issue']}
          initialBackAnchor={initialBackAnchor}
          viewer={data?.safeViewer ?? null}
          isRepoOwnerEnterpriseManaged={repository?.isOwnerEnterpriseManaged}
          userNameDisplayConfiguration={repository.owner.userNameDisplayConfiguration}
          routeQueryKey={routeQueryKey}
          timelinePage={timelinePage}
          optionConfig={optionConfig}
        />
      </RelayEnvironmentProvider>
      {/* The dashboard's panel: already owns the lazy Relay chunk, error boundary and issue swap. */}
      <DashboardIssueSidePanel
        issue={panelIssue}
        onClose={closePanel}
        onExpand={expandPanel}
        onIssueDelete={closePanel}
        onSubIssueSelect={showPanelIssue}
        returnFocusRef={panelReturnFocusRef}
      />
    </BaseAnalyticsWrapper>
  )
}
